import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { LogService } from 'src/app/baseServices/logging/log.service';
import { GlobalConstants } from 'src/app/constant/constant';
import { AppEventType } from 'src/app/enums/app-events.enum';
import { environment } from 'src/environments/environment';
import { Md5 } from 'ts-md5';
import { EventBusService } from '../event-bus.service';
import { OfflinePasswordService } from '../offline-password.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  userExpireDate: number;
  tokenExpiryAt: number;
  timeoutInterval: NodeJS.Timeout;
  private authenticated = false;
  private onUserLoggedIn = new Subject();
  onUserLoggedIn$ = this.onUserLoggedIn.asObservable();

  constructor(
    protected http: HttpClient, 
    private router: Router, 
    private eventBusService: EventBusService,
    private logService: LogService,
    private offlinePassword: OfflinePasswordService,
  ) {}

  login(email: string, password: string, force: boolean) {
    const uri = environment.authApiUrl + '/Auth/login';
    const md5Password = Md5.hashStr(password);

    return this.http
      .post<any>(uri, {
        email,
        password: md5Password,
        apiKey: environment.authApiKey,
        force,
      })
      .pipe(
        map(async (user) => {
          this.userExpireDate = new Date().getTime();
          this.tokenExpiryAt = 28800000; // milliseconds

          if (user && user.AccessToken) {
            this.authenticated = true;
            localStorage.setItem(GlobalConstants.IS_AUTHENTICATED, JSON.stringify(this.authenticated));
            localStorage.setItem(GlobalConstants.ACCESS_TOKEN, user.AccessToken);

            this.timeoutInterval = setTimeout(() => {
              this.logout();
            }, this.tokenExpiryAt);

            localStorage.setItem(
              GlobalConstants.USER_KEY,
              JSON.stringify(user)
            );

            try {
              await this.offlinePassword.hashAndStore(password);
            } catch (e) {
              this.logService.error('Auth.OfflinePasswordStoreFailed', e as any);
            }

            this.eventBusService.emit({ type: AppEventType.LOGIN_SUCCESS, data: null });
            this.logService.event('Auth.LoginSuccess', { issuedAt: this.userExpireDate, tokenTtlMs: this.tokenExpiryAt });
          }
          return user;
        })
      );
  }

  async logout() {
    localStorage.setItem(GlobalConstants.IS_AUTHENTICATED, JSON.stringify(false));
    localStorage.removeItem(GlobalConstants.USER_KEY);
    this.offlinePassword.clear();
    this.eventBusService.emit({type: AppEventType.LOGOUT_SUCCESS, data: null});
    this.router.navigate(['auth/login'], { replaceUrl: true });
    this.logService.event('Auth.Logout');
  }

  public isAuthenticated(): boolean {
    try {
      return JSON.parse(localStorage.getItem(GlobalConstants.IS_AUTHENTICATED));
    } catch {
      return false;
    }
  }

  getRememberEmailStatus(): boolean {
    try {
      return JSON.parse(localStorage.getItem(GlobalConstants.REMEMBER_EMAIL));
    } catch {
      return false;
    }
  }

  setRememberEmailStatus(status: boolean) {
    localStorage.setItem(GlobalConstants.REMEMBER_EMAIL, JSON.stringify(status));
  }

  /**
   * Attempt offline password verification against the locally stored slow hash.
   * Returns true if the stored hash exists and matches the provided password.
   * Intended for features like re-auth gating (e.g., unlocking sensitive cached data while offline).
   */
  async verifyOfflinePassword(password: string): Promise<boolean> {
    return this.offlinePassword.verify(password);
  }
}
